import { useState } from 'react'
import { trpc } from '@/config/trpc'
import { EventEditModal } from '@/components/calendar/EventEditModal'
import { eventToScheduledPostUpdate } from '@/lib/calendar/post-adapter'
import { type Event } from '@/lib/calendar/types'
import { toast } from 'sonner'

// `forceReadOnly` is set for shared (token-based) viewers: it pins every post
// to read-only regardless of status/date and swaps the JWT-only account lookup
// for the share-safe `platform.listAccountsForDisplay` query.
export function usePostEditing({ forceReadOnly = false }: { forceReadOnly?: boolean } = {}) {
  const utils = trpc.useUtils()
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)

  const { data: scheduledPosts } = trpc.scheduledPost.list.useQuery()

  // Full per-platform auth data (JWT-only) backs the owner's editable modal.
  const authQueryOpts = { enabled: !forceReadOnly } as const
  const { data: linkedinData } = trpc.platform.isAuthenticated.useQuery(
    { platform: 'linkedin' },
    authQueryOpts,
  )
  const { data: instagramData } = trpc.platform.isAuthenticated.useQuery(
    { platform: 'instagram' },
    authQueryOpts,
  )
  const { data: tiktokData } = trpc.platform.isAuthenticated.useQuery(
    { platform: 'tiktok' },
    authQueryOpts,
  )
  const { data: youtubeData } = trpc.platform.isAuthenticated.useQuery(
    { platform: 'youtube' },
    authQueryOpts,
  )

  // Slim, share-safe account display (avatar + handle) for the read-only preview.
  const { data: sharedAccounts } = trpc.platform.listAccountsForDisplay.useQuery(undefined, {
    enabled: forceReadOnly,
  })

  const { mutateAsync: updateScheduledPost } = trpc.scheduledPost.update.useMutation()
  const { mutateAsync: deleteScheduledPost } = trpc.scheduledPost.delete.useMutation()
  const { mutateAsync: removeTrackedPost } = trpc.trackedPost.remove.useMutation()

  const getAccountForEvent = (event: Event) => {
    if (!event.postId) return undefined
    const post = scheduledPosts?.find((p) => p.id === event.postId)
    if (!post) return undefined

    if (forceReadOnly) {
      return sharedAccounts?.find(
        (acc) => acc.platform === post.platform && acc.id === post.accountId,
      )
    }

    const accountData = (() => {
      switch (post.platform) {
        case 'linkedin':
          return linkedinData
        case 'instagram':
          return instagramData
        case 'tiktok':
          return tiktokData
        case 'youtube':
          return youtubeData
        default:
          return undefined
      }
    })()

    if (accountData?.isAuthenticated === true) {
      return accountData.accounts.find((acc) => acc.id === post.accountId)
    }
    return undefined
  }

  const handleUpdate = async (updatedEvent: Event) => {
    try {
      await updateScheduledPost(eventToScheduledPostUpdate(updatedEvent))
      utils.scheduledPost.list.invalidate()
      toast.success('Post updated successfully')
      setSelectedEvent(null)
    } catch (error) {
      toast.error('Failed to update post', { description: String(error) })
    }
  }

  const handleDelete = async (eventId: string) => {
    const id = selectedEvent?.postId ?? Number(eventId)
    if (!id) return
    try {
      await deleteScheduledPost({ id })
      utils.scheduledPost.list.invalidate()
      toast.success('Post deleted successfully')
      setSelectedEvent(null)
    } catch (error) {
      toast.error('Failed to delete post', { description: String(error) })
    }
  }

  // Escape hatch for the publish lock: a post deleted on the platform (or
  // published by mistake) keeps its workspace locked forever unless the
  // tracked_post row goes. Removing it only affects CASSI, never the platform.
  const handleRemoveTracked = async (trackedPostId: number) => {
    try {
      await removeTrackedPost({ trackedPostId })
      utils.trackedPost.list.invalidate()
      utils.workspace.postStatus.invalidate()
      toast.success('Post removed from CASSI')
      setSelectedEvent(null)
    } catch (error) {
      toast.error('Failed to remove post', { description: String(error) })
    }
  }

  const isReadOnly = (() => {
    if (!selectedEvent) return false
    if (forceReadOnly) return true
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const eventDate = new Date(selectedEvent.date)
    eventDate.setHours(0, 0, 0, 0)
    return (
      eventDate < today ||
      selectedEvent.status === 'published' ||
      selectedEvent.source === 'tracked'
    )
  })()

  const modal = (
    <EventEditModal
      isOpen={selectedEvent !== null}
      onClose={() => setSelectedEvent(null)}
      event={selectedEvent}
      onEventUpdate={handleUpdate}
      onEventDelete={handleDelete}
      account={selectedEvent ? getAccountForEvent(selectedEvent) : undefined}
      platform={selectedEvent?.platform ?? 'linkedin'}
      readOnly={isReadOnly}
      onTrackedPostRemove={forceReadOnly ? undefined : handleRemoveTracked}
    />
  )

  return { selectEvent: setSelectedEvent, modal }
}
